import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Resources from "../../../types/Resources";
import { debounce } from "../../../common";
import WorkPermitSearchResult from "../../../types/WorkPermitSearchResult";
import { searchWorkPermitsForConnection } from "../../../apiClient";
import { PermitType } from "../../../common/enums";
import ProblemDetails from "../../../types/responses/ProblemDetails";
import Result from "../../../types/Result";
import Swal from "sweetalert2";
import RaftChecklistFormState from "../../../types/forms/Permits/Conditions/RaftChecklistFormState";
import TextInput from "../../Common/TextInput";

type RaftChecklistModalProps = {
    resources: Resources;
    formState: RaftChecklistFormState;
    onChange: (fn: (prevValue: RaftChecklistFormState) => RaftChecklistFormState) => void;
    show: boolean;
    onHide: () => void;
    canCreateUnderWaterWorkPermits: boolean;
    canManageContent: boolean;
};

function RaftChecklistModal({
    resources,
    formState,
    onChange,
    show,
    onHide,
    canCreateUnderWaterWorkPermits,
    canManageContent
}: RaftChecklistModalProps) {
    const [searchInput, setSearchInput] = useState("");
    const [searchResult, setSearchResult] = useState<WorkPermitSearchResult[]>([]);
    const [searchResultDropdownIsOpen, setSearchResultDropdownIsOpen] = useState(false);

    const searchSectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function clickHandler(e: MouseEvent) {
            if (!searchSectionRef.current || !searchSectionRef.current.contains(e.target as Node)) {
                setSearchResultDropdownIsOpen(false);
            }
        }

        document.addEventListener("mousedown", clickHandler);
        return () => document.removeEventListener("mousedown", clickHandler);
    }, []);

    const debounceSearch = useCallback(
        debounce(async (input: string[]) => {
            if (input.length === 0 || !input.some((str) => str && str.length > 0)) {
                setSearchResultDropdownIsOpen(false);
                return;
            }

            const inputString: string = input[0];

            const workPermitSearchResult: Result<WorkPermitSearchResult[], ProblemDetails> =
                await searchWorkPermitsForConnection(inputString, PermitType.UnderWaterWorkPermit);

            if (workPermitSearchResult.failed) {
                console.error(workPermitSearchResult.error);
                setSearchResultDropdownIsOpen(false);
                Swal.fire({
                    icon: "error",
                    text: resources.get("searchError")
                });

                return;
            }

            setSearchResult(workPermitSearchResult.value);
            setSearchResultDropdownIsOpen(true);
        }, 300),
        []
    );

    async function searchInputChangeHandler(newValue: string) {
        setSearchInput(newValue);
        debounceSearch(newValue);
    }

    function sequenceIdentifierChangeHandler(newValue: string) {
        onChange((prevValue) => ({
            ...prevValue,
            underWaterWorkPermitSequenceIdentifier: {
                ...prevValue.underWaterWorkPermitSequenceIdentifier,
                value: newValue
            }
        }));
    }

    function searchResultClickHandler(clickedResultSequenceIdentifier: string) {
        sequenceIdentifierChangeHandler(clickedResultSequenceIdentifier);
        setSearchInput("");
        setSearchResultDropdownIsOpen(false);
    }

    const searchResultListItems = searchResult.map((r) => {
        const positionComments = r.positions
            .filter((r) => r.comment)
            .map((p) => p.comment)
            .join(", ");

        return (
            <li
                key={r.sequenceIdentifier}
                onClick={() => searchResultClickHandler(r.sequenceIdentifier)}
                className="list-group-item p-2 border-top"
            >
                {r.sequenceIdentifier}, {r.status}, {positionComments}, {r.companyName}, {r.description}
            </li>
        );
    });

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("ChecklistaRaft")}</h1>
            </Modal.Header>
            <Modal.Body>
                {canCreateUnderWaterWorkPermits ? (
                    <p className="fw-bold">
                        <span className="me-1">{resources.get("RaftChecklistInfo0")}</span>
                        <a href="/Permits/UnderWaterWorkPermit" target="_blank" className="me-1">
                            {resources.get("RaftChecklistInfo1")}
                        </a>
                        <span>{resources.get("RaftChecklistInfo2")}</span>
                    </p>
                ) : (
                    <div className="alert alert-danger fw-bold" style={{ fontSize: "18px" }}>
                        <span className="me-1">
                            {resources.get("RaftChecklist_PortControlCreatePermissionMissing0")}
                        </span>
                        <a href="mailto:hetarbeteneh@portgot.se" target="_blank" className="me-1">
                            hetarbeteneh@portgot.se
                        </a>
                        <span>{resources.get("RaftChecklist_PortControlCreatePermissionMissing1")}</span>
                    </div>
                )}
                <TextInput
                    inputId="under-water-work-permit-sequence-identifier"
                    labelText={resources.get("UnderWaterWorkPermitId")}
                    onChange={s => sequenceIdentifierChangeHandler(s)}
                    readOnly={!canManageContent}
                    value={formState.underWaterWorkPermitSequenceIdentifier.value ?? ""}
                    errorMessage={formState.underWaterWorkPermitSequenceIdentifier.errorMessage}
                />

                <div className="mt-3" ref={searchSectionRef}>
                    {canManageContent ? (
                        <>
                            <label htmlFor="under-water-work-permit-search-input">{resources.get("SearchLabel")}</label>
                            <input
                                id="under-water-work-permit-search-input"
                                placeholder={resources.get("SearchPlaceholder")}
                                type="text"
                                className="form-control"
                                value={searchInput ?? ""}
                                onChange={(e) => searchInputChangeHandler(e.target.value)}
                            />
                            <div
                                id="under-water-work-permit-search-results"
                                className="list-group work-permit-search-results"
                                style={{ maxHeight: "300px", overflowY: "auto" }}
                            >
                                {!searchResultDropdownIsOpen ? null : searchResult.length === 0 ? (
                                    <div>{resources.get("NoPermitSearchResults")}</div>
                                ) : (
                                    <ul className="list-unstyled work-permit-search-results border rounded">
                                        {searchResultListItems}
                                    </ul>
                                )}
                            </div>
                        </>
                    ) : null}
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default RaftChecklistModal;
